<template>
  <div class="ma-5">
    <v-data-table
      :headers="headers"
      :items="pessoasFiltradas"
      :search="search"
      :height="430"
      :fixed-header="true"
      :loading="carregando"
      class="elevation-3"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-toolbar-title>Minhas Pessoas — Status na Parceira</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>

          <v-chip
            v-if="parceiraNome && parceiraDisponivel"
            small
            color="green darken-1"
            text-color="white"
            class="mr-3"
          >
            <v-icon left x-small>mdi-link-variant</v-icon>
            Parceira: {{ parceiraNome }}
          </v-chip>
          <v-chip
            v-else-if="parceiraNome && !parceiraDisponivel"
            small
            color="orange darken-1"
            text-color="white"
            class="mr-3"
          >
            <v-icon left x-small>mdi-cloud-off-outline</v-icon>
            Parceira indisponível
          </v-chip>
          <v-chip v-else small color="grey" text-color="white" class="mr-3">
            <v-icon left x-small>mdi-link-off</v-icon>
            Sem parceira configurada
          </v-chip>

          <v-spacer></v-spacer>

          <v-text-field
            class="mr-4"
            v-model="search"
            append-icon="mdi-magnify"
            label="Pesquisar"
            single-line
            hide-details
          ></v-text-field>

          <v-select
            v-model="filtroEstado"
            :items="filtroEstadoOpcoes"
            label="Filtrar por estado"
            dense
            hide-details
            outlined
            class="mr-4 mt-3 filtro-estado"
            style="max-width: 220px"
          ></v-select>

          <v-btn
            color="primary"
            :disabled="pessoasPendentes.length === 0 || !parceiraDisponivel"
            @click="replicarTodasPendentes"
          >
            <v-icon left>mdi-cloud-upload-outline</v-icon>
            Replicar pendentes ({{ pessoasPendentes.length }})
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:item.presente_local="{ }">
        <v-icon color="green" small>mdi-check-circle</v-icon>
      </template>

      <template v-slot:item.compartilhamento="{ item }">
        <span :title="tooltipCompartilhamento(item)">
          <v-icon v-if="estadoCompartilhamento(item) === 'compartilhada'" color="green" small>mdi-check-circle</v-icon>
          <v-icon v-else-if="estadoCompartilhamento(item) === 'pendente'" color="amber darken-2" small>mdi-clock-outline</v-icon>
          <v-icon v-else-if="estadoCompartilhamento(item) === 'exclusiva'" color="blue darken-1" small>mdi-lock</v-icon>
          <v-icon v-else color="grey" small>mdi-help-circle</v-icon>
          <span class="caption ml-1">{{ rotuloCompartilhamento(item) }}</span>
        </span>
      </template>

      <template v-slot:item.presente_parceira="{ item }">
        <v-icon v-if="item.presente_parceira === true && !item.conflito" color="green" small>mdi-check-circle</v-icon>
        <v-icon v-else-if="item.conflito" color="amber darken-2" small :title="`Cadastrada por: ${item.nome_assessor_remoto || ''}`">mdi-alert-circle</v-icon>
        <v-icon v-else-if="item.presente_parceira === false" color="red" small>mdi-close-circle</v-icon>
        <v-icon v-else color="grey" small title="Status desconhecido (parceira indisponível)">mdi-help-circle</v-icon>
      </template>

      <template v-slot:item.action="{ item }">
        <!-- Indefinida: dois botões -->
        <template v-if="estadoCompartilhamento(item) === 'indefinida'">
          <v-btn x-small color="green darken-1" dark class="mr-1" :disabled="!parceiraDisponivel" @click="marcarCompartilhada(item)">
            <v-icon left x-small>mdi-cloud-upload-outline</v-icon>
            Compartilhar
          </v-btn>
          <v-btn x-small color="blue darken-1" dark :disabled="!parceiraDisponivel" @click="marcarExclusiva(item)">
            <v-icon left x-small>mdi-lock</v-icon>
            Exclusiva
          </v-btn>
        </template>

        <!-- Exclusiva: pode voltar a compartilhar -->
        <v-btn
          v-else-if="estadoCompartilhamento(item) === 'exclusiva'"
          x-small color="green darken-1" dark
          :disabled="!parceiraDisponivel"
          @click="marcarCompartilhada(item)"
        >
          <v-icon left x-small>mdi-cloud-upload-outline</v-icon>
          Compartilhar
        </v-btn>

        <!-- Pendente: replicar agora -->
        <v-btn
          v-else-if="estadoCompartilhamento(item) === 'pendente'"
          x-small color="primary"
          :disabled="!parceiraDisponivel"
          @click="replicarUma(item)"
        >
          <v-icon left x-small>mdi-cloud-upload-outline</v-icon>
          Replicar
        </v-btn>

        <!-- Compartilhada: pode tornar exclusiva (vai apagar de B) -->
        <template v-else-if="estadoCompartilhamento(item) === 'compartilhada'">
          <v-btn
            x-small color="blue darken-1" dark
            :disabled="!parceiraDisponivel"
            @click="confirmarExclusivar(item)"
          >
            <v-icon left x-small>mdi-lock</v-icon>
            Tornar exclusiva
          </v-btn>
        </template>

        <span v-else-if="item.conflito" class="caption amber--text text--darken-3">
          Conflito
        </span>
      </template>
    </v-data-table>

    <!-- Dialog de progresso/sumário do lote -->
    <v-dialog v-model="dialogLote" persistent max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Replicação em lote</span>
        </v-card-title>
        <v-card-text>
          <div v-if="loteEmAndamento">
            <p>Replicando pessoas pendentes...</p>
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
          </div>
          <div v-else>
            <p><strong>Replicadas:</strong> {{ resumoLote.ok }}</p>
            <p v-if="resumoLote.conflitos && resumoLote.conflitos.length">
              <strong>Conflitos:</strong> {{ resumoLote.conflitos.length }}
            </p>
            <p v-if="resumoLote.falhas && resumoLote.falhas.length">
              <strong>Falhas:</strong> {{ resumoLote.falhas.length }}
            </p>
            <p v-if="resumoLote.ignorados && resumoLote.ignorados.length">
              <strong>Ignoradas (sem flag de compartilhamento):</strong> {{ resumoLote.ignorados.length }}
            </p>
            <p v-if="resumoLote.abortado" class="red--text">
              Abortado: {{ resumoLote.motivo }}
            </p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="loteEmAndamento" @click="dialogLote = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import ParceiraAPI from "./../../api/parceira";

export default {
  name: "MinhasPessoas",
  data() {
    return {
      search: "",
      carregando: false,
      filtroEstado: "todas",
      pessoas: [],
      parceiraNome: "",
      parceiraDisponivel: false,
      dialogLote: false,
      loteEmAndamento: false,
      resumoLote: {
        ok: 0,
        conflitos: [],
        falhas: [],
        ignorados: [],
        abortado: false,
        motivo: "",
      },
      filtroEstadoOpcoes: [
        { text: "Todas", value: "todas" },
        { text: "Apenas pendentes", value: "pendente" },
        { text: "Apenas indefinidas", value: "indefinida" },
        { text: "Apenas exclusivas", value: "exclusiva" },
        { text: "Apenas conflitos", value: "conflito" },
      ],
      headers: [
        { text: "ID", value: "idpessoa", width: 70 },
        { text: "Nome", value: "nome_completo" },
        { text: "Telefone", value: "telefone", width: 130 },
        { text: "Aqui", value: "presente_local", sortable: false, width: 60, align: "center" },
        { text: "Compartilhamento", value: "compartilhamento", sortable: false, width: 170, align: "left" },
        { text: "Parceira", value: "presente_parceira", sortable: false, width: 90, align: "center" },
        { text: "Ação", value: "action", sortable: false, width: 220, align: "center" },
      ],
    };
  },

  computed: {
    pessoasFiltradas() {
      const f = this.filtroEstado;
      if (f === "todas") return this.pessoas;
      if (f === "conflito") return this.pessoas.filter((p) => p.conflito);
      return this.pessoas.filter((p) => this.estadoCompartilhamento(p) === f);
    },

    pessoasPendentes() {
      return this.pessoas.filter((p) => this.estadoCompartilhamento(p) === "pendente");
    },
  },

  created() {
    this.carregar();
  },

  methods: {
    estadoCompartilhamento(item) {
      const c = item.compartilhar_parceira;
      if (c === 1) {
        return item.replicado_em ? "compartilhada" : "pendente";
      }
      if (c === 0) return "exclusiva";
      return "indefinida";
    },

    rotuloCompartilhamento(item) {
      const e = this.estadoCompartilhamento(item);
      if (e === "compartilhada") return "Compartilhada";
      if (e === "pendente") return "Pendente";
      if (e === "exclusiva") return "Exclusiva";
      return "Indefinida";
    },

    tooltipCompartilhamento(item) {
      const e = this.estadoCompartilhamento(item);
      if (e === "compartilhada") return `Replicada para ${this.parceiraNome || "a parceira"}`;
      if (e === "pendente") return "Marcada para compartilhar, aguardando replicação";
      if (e === "exclusiva") return "Não será replicada";
      return "Decida o compartilhamento";
    },

    carregar() {
      this.carregando = true;
      const headers = this.Helpers.iniciarRequisicao();

      ParceiraAPI.minhasPessoasStatus(headers)
        .then((response) => {
          const token = response.data.token;
          if (token) localStorage.setItem("token", token);

          this.pessoas = response.data.pessoas || [];
          if (response.data.parceira) {
            this.parceiraNome = response.data.parceira.nome || "";
            this.parceiraDisponivel = !!response.data.parceira.disponivel;
          } else {
            this.parceiraNome = "";
            this.parceiraDisponivel = false;
          }
        })
        .catch(() => {
          this.Helpers.exibirMensagem(
            "Falha ao carregar status das pessoas.",
            "error",
            5000
          );
        })
        .finally(() => {
          this.carregando = false;
          this.$store.dispatch("loading/habilitarLoading", false);
        });
    },

    replicarUma(item) {
      const headers = this.Helpers.getHeaderRequisicao();
      ParceiraAPI.replicarPessoa(headers, { idpessoa: item.idpessoa })
        .then(() => {
          this.Helpers.exibirMensagem(
            `${item.nome_completo} replicada com sucesso.`,
            "success",
            4000
          );
          this.carregar();
        })
        .catch((error) => {
          const status = error.response && error.response.status;
          const err = error.response && error.response.data && error.response.data.error;
          if (status === 422 && err === "conflito_assessor") {
            const nome = error.response.data.nome_assessor_remoto || "outro assessor";
            this.Helpers.exibirMensagem(
              `Conflito: cadastrada na parceira sob ${nome}.`,
              "warning",
              7000
            );
            this.carregar();
          } else if (status === 422 && err === "pessoa_marcada_exclusiva") {
            this.Helpers.exibirMensagem(
              "Pessoa está marcada como exclusiva. Marque-a como 'Compartilhar' antes.",
              "warning",
              6000
            );
          } else if (status === 503) {
            this.Helpers.exibirMensagem(
              "Parceira indisponível no momento.",
              "warning",
              5000
            );
          } else {
            this.Helpers.exibirMensagem(
              "Falha ao replicar.",
              "error",
              5000
            );
          }
        });
    },

    marcarCompartilhada(item) {
      const headers = this.Helpers.getHeaderRequisicao();
      ParceiraAPI.marcarCompartilhada(headers, { idpessoa: item.idpessoa })
        .then(() => {
          this.Helpers.exibirMensagem(
            `${item.nome_completo} marcada como compartilhada e enviada à parceira.`,
            "success",
            4000
          );
          this.carregar();
        })
        .catch((error) => {
          const status = error.response && error.response.status;
          const err = error.response && error.response.data && error.response.data.error;
          if (status === 422 && err === "conflito_assessor") {
            const nome = error.response.data.nome_assessor_remoto || "outro assessor";
            this.Helpers.exibirMensagem(
              `Marcada como compartilhada, mas há conflito na parceira (já cadastrada sob ${nome}).`,
              "warning",
              7000
            );
            this.carregar();
          } else if (status === 503) {
            this.Helpers.exibirMensagem(
              "Marcada como compartilhada, mas a parceira está indisponível. Será replicada quando voltar.",
              "warning",
              6000
            );
            this.carregar();
          } else {
            this.Helpers.exibirMensagem("Falha ao marcar como compartilhada.", "error", 5000);
          }
        });
    },

    marcarExclusiva(item) {
      const headers = this.Helpers.getHeaderRequisicao();
      ParceiraAPI.marcarExclusiva(headers, { idpessoa: item.idpessoa })
        .then(() => {
          this.Helpers.exibirMensagem(
            `${item.nome_completo} marcada como exclusiva.`,
            "success",
            4000
          );
          this.carregar();
        })
        .catch((error) => {
          const status = error.response && error.response.status;
          const err = error.response && error.response.data && error.response.data.error;
          if (status === 422 && err === "conflito_assessor") {
            const nome = error.response.data.nome_assessor_remoto || "outro assessor";
            this.Helpers.exibirMensagem(
              `Não foi possível tornar exclusiva: na parceira está sob ${nome}.`,
              "warning",
              7000
            );
          } else if (status === 503) {
            this.Helpers.exibirMensagem(
              "Parceira indisponível — não é seguro marcar exclusiva agora. Tente quando voltar.",
              "warning",
              6000
            );
          } else {
            this.Helpers.exibirMensagem("Falha ao marcar como exclusiva.", "error", 5000);
          }
        });
    },

    confirmarExclusivar(item) {
      const msg = `Isto irá APAGAR "${item.nome_completo}" da base parceira (${this.parceiraNome}). Tem certeza?`;
      if (window.confirm(msg)) {
        this.marcarExclusiva(item);
      }
    },

    replicarTodasPendentes() {
      const ids = this.pessoasPendentes.map((p) => p.idpessoa);
      if (ids.length === 0) return;

      this.dialogLote = true;
      this.loteEmAndamento = true;
      this.resumoLote = {
        ok: 0,
        conflitos: [],
        falhas: [],
        ignorados: [],
        abortado: false,
        motivo: "",
      };

      const headers = this.Helpers.getHeaderRequisicao();
      ParceiraAPI.replicarPessoasLote(headers, { idpessoas: ids })
        .then((response) => {
          this.resumoLote = response.data || this.resumoLote;
        })
        .catch((error) => {
          const data = error.response && error.response.data;
          if (data) {
            this.resumoLote = data;
          } else {
            this.resumoLote.abortado = true;
            this.resumoLote.motivo = "erro_inesperado";
          }
        })
        .finally(() => {
          this.loteEmAndamento = false;
          this.carregar();
        });
    },
  },
};
</script>
<style scoped>
.filtro-estado >>> .v-input__slot {
  min-height: 36px;
}
</style>
